import { useEffect, useRef, useState } from 'react';

/**
 * 자주 바뀌는 판 번호를 `ms`에 한 번으로 묶는다 (D-C5 · D-C6).
 *
 * 실시간 반영은 1초에 수십 번 판을 올린다. 그 판에 5만 행 훑기나 서버 조회를 매달면 판마다 돈다.
 * 이 훅은 **처음 바뀐 것은 곧바로** 돌려주고, 그 뒤 `ms` 안에 또 바뀐 것은 모아 두었다가 `ms`가
 * 지난 순간 마지막 값 하나로 돌려준다. 조용하다가 한 번 바뀌면 늦지 않고, 몰려 와도 `ms`에 한 번이다.
 * 마지막 값은 반드시 돌려주므로 멈춘 뒤의 화면은 묶지 않았을 때와 같다.
 *
 * 시각은 단조 시계(`performance.now()`)로 잰다 (R62-3). 벽시계가 뒤로 가면 기다림이 그만큼 늘어
 * 60초 되돌리면 61초를 기다렸다. 기다림은 어떤 경우에도 `ms`를 넘지 않게 자른다.
 */
export function useCoalesced<T>(value: T, ms: number): T {
  const [shown, setShown] = useState(value);
  const lastAt = useRef(Number.NEGATIVE_INFINITY);

  useEffect(() => {
    if (Object.is(value, shown)) return;
    const wait = Math.min(ms, lastAt.current + ms - performance.now());
    if (!(wait > 0)) {
      lastAt.current = performance.now();
      setShown(value);
      return;
    }
    // 기다리는 동안 또 바뀌면 이 예약을 거두고 같은 시각으로 다시 건다. 도착 시각은 밀리지 않는다
    const timer = setTimeout(() => {
      lastAt.current = performance.now();
      setShown(value);
    }, wait);
    return () => clearTimeout(timer);
  }, [value, shown, ms]);

  return shown;
}
